import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import axios from "axios";
import FormField from '../../../../dynamicFormBuilder/FormField';
import MasterDataTab from '../../../../id/common/MasterDataTab';
import data from "../../../../assets/apiRoutes.json";
import { loadLookupFromDataSource, loadLookupFromStaticDataSource } from '../../../../common/Utils';
import { CUSTOMER_STATUS_OPTIONS, ID_TYPE_CUSTOMER, ID_MEMBER_TYPE_CONTACT, EMPTY_GUID } from '../../../../const/constants';
import '../../../../common/css/searchPage.css';

// Additional tab layout — Miscellaneous + A/R Control Account sections
// (read-only report view). Coded fields render as lookup-bound selects so the
// stored rowid resolves to its code, like the Customer Data Entry screen.
const AdditionalLayout = [
    {
        "groupId": 1,
        "label": "Miscellaneous",
        "fields": [
            { "name": "CurrentBalance", "tableName": "IDCUSTOMER", "label": "Balance", "type": "number", "format": "$ #,##0.00", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerR_TAXCODE", "tableName": "IDCUSTOMER", "label": "Tax Code", "type": "select", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerR_TERMS", "tableName": "IDCUSTOMER", "label": "Terms Code", "type": "select", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerSTATUS", "tableName": "IDCUSTOMER", "label": "Status", "type": "select", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerRESALENUMBER", "tableName": "IDCUSTOMER", "label": "Resale #", "type": "text", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerCOMMENT", "tableName": "IDCUSTOMER", "label": "Comment", "type": "text", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
        ]
    },
    {
        "groupId": 2,
        "label": "A/R Control Account",
        "fields": [
            { "name": "CustomerARACCOUNT", "tableName": "IDCUSTOMER", "label": "Account", "type": "select", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerR_GLDEPARTMENT", "tableName": "IDCUSTOMER", "label": "Department", "type": "select", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerR_GLFUND", "tableName": "IDCUSTOMER", "label": "Fund", "type": "select", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
            { "name": "CustomerR_CAJOB", "tableName": "IDCUSTOMER", "label": "Job", "type": "select", "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
        ]
    }
];

const ADDITIONAL_FIELDS = [
    "CustomerR_TAXCODE", "CustomerR_TERMS", "CustomerSTATUS", "CustomerRESALENUMBER", "CustomerCOMMENT",
    "CustomerARACCOUNT", "CustomerR_GLDEPARTMENT", "CustomerR_GLFUND", "CustomerR_CAJOB",
];

const emptyFormFields = () => {
    const acc = { CurrentBalance: 0 };
    ADDITIONAL_FIELDS.forEach((key) => { acc[key] = ''; });
    return acc;
};

const AdditionalTab = (props) => {
    const accessToken = props.oidc.user.access_token;
    const currentUser = props.oidc.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const formRefs = useRef({});
    const [formFields, setformFields] = useState(emptyFormFields());

    // Customer master + control-account references — reuse the customer already
    // loaded by PrimaryTab (no second GetCustomerDataByRowId call).
    useEffect(() => {
        const customer = props.customer;
        if (customer && customer.ROWID) {
            setformFields((prev) => {
                const next = { ...prev };
                ADDITIONAL_FIELDS.forEach((key) => { next[key] = customer[key] ?? ''; });
                return next;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.customer]);

    // Current A/R balance from the summary totals endpoint (not provided by the
    // shared customer payload).
    useEffect(() => {
        if (!props.id) {
            return;
        }
        axios.get(`${window._env_.REACT_APP_APIS}/${data.ID.GetSummaryTotals}?customerId=${props.id}`, {
            headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
        }).then((res) => {
            const totals = res.data;
            if (totals) {
                setformFields((prev) => ({ ...prev, CurrentBalance: totals.currentBalance ?? 0 }));
            }
        }).catch((err) => {
            console.error("Failed to load customer summary totals:", err);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id]);

    const handleFieldChange = useCallback((name, value) => {
    }, []);

    const handleFormLoad = useCallback(() => {
        const api = (route) => `${window._env_.REACT_APP_APIS}/${route}`;
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_TAXCODE", api(data.ID.GetIDTax));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_TERMS", api(data.ID.GetIDTerms));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerARACCOUNT", api(data.AC.GetAcAccounts));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_GLDEPARTMENT", api(data.AC.GetDepartments));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_GLFUND", api(data.FA.GetFafunds));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_CAJOB", api(data.CA.GetCaJobs));
        loadLookupFromStaticDataSource("CustomerSTATUS", CUSTOMER_STATUS_OPTIONS, formFields, formRefs, "id", "name");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <FormField
                currrentUser={currentUser}
                layoutId='2F8B6D14-73A5-4C9E-9B27-1E4A5D8C60F3'
                layoutName='TransactionsByCustomerAdditionalForm'
                defaultFormSchema={AdditionalLayout}
                formFields={formFields}
                setformFields={setformFields}
                handleFieldValueChange={handleFieldChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={formRefs}
                onFormLoad={handleFormLoad}
                noSpacingBeforeFirstRow={true}
            />
            {props.id && props.id !== EMPTY_GUID && (
                <div>
                    <MasterDataTab
                        RIDMaster={props.id}
                        IDType={ID_TYPE_CUSTOMER}
                        IDMemeberType={ID_MEMBER_TYPE_CONTACT}
                        TabName="AllContacts"
                        gridKey='7A3E9C52-1D64-4B08-9F2A-6C5E0B71D3A9'
                    />
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(AdditionalTab);
